/** IoT 四域 API（对接 /api/device | /api/data | /api/alert | /api/ai） */

import request from './request';
import type {
  PageResult,
  DeviceDto,
  DeviceShadowDto,
  IotReadingDto,
  IotAlertDto,
  AlertRuleDto,
  NotifyConfigDto,
  DeviceGroupDto,
  DeviceTypeDto,
  DeviceTypeSaveRequest,
  AiConversationDto,
  KnowledgeBaseDto,
  KbDocumentDto,
  AnomalyDetectionRuleDto,
  DeviceListQuery,
  AlertListQuery,
  ReadingQuery,
  KbListQuery,
  AiChatRequest,
  ChatSessionDto,
  DeviceBindRequest,
  DeviceSaveRequest,
  ContainerBindRequest,
  ContainerBindResp,
  ActiveBindVo,
  RealtimeItem,
  ContainerRealtimeVo,
  TempReportItem,
  TempReportResult,
} from '@bluplanet/shared';

// ==================== 设备域 (/api/device) ====================

const DEVICE_BASE = '/device';

/** 设备列表��分页+搜索）: GET /api/device/list */
export function listDevices(params: DeviceListQuery) {
  return request<PageResult<DeviceDto>>({ url: DEVICE_BASE + '/list', method: 'GET', params });
}

/** 设备详情: GET /api/device/{id} */
export function getDevice(id: string) {
  return request<DeviceDto>({ url: DEVICE_BASE + '/' + id, method: 'GET' });
}

/** 设备影子: GET /api/device/shadow/{deviceId} */
export function getDeviceShadow(deviceId: string) {
  return request<DeviceShadowDto>({ url: DEVICE_BASE + '/shadow/' + deviceId, method: 'GET' });
}

/** 绑定设备: POST /api/device/bind */
export function bindDevice(body: DeviceBindRequest) {
  return request<void>({ url: DEVICE_BASE + '/bind', method: 'POST', data: body });
}

/** 新增/编辑设备（合一）: POST /api/device */
export function saveDevice(body: DeviceSaveRequest) {
  return request<DeviceDto>({ url: DEVICE_BASE, method: 'POST', data: body });
}

/** 删除设备: DELETE /api/device/{id} */
export function removeDevice(id: string) {
  return request<void>({ url: DEVICE_BASE + '/' + id, method: 'DELETE' });
}

/** 设备分组列表: GET /api/device/group/list */
export function listDeviceGroups(params: { page?: number; pageSize?: number; keyword?: string }) {
  return request<PageResult<DeviceGroupDto>>({ url: DEVICE_BASE + '/group/list', method: 'GET', params });
}

/** 创建设备分组: POST /api/device/group */
export function createDeviceGroup(body: Partial<DeviceGroupDto>) {
  return request<void>({ url: DEVICE_BASE + '/group', method: 'POST', data: body });
}

/** 更新设备分组: PUT /api/device/group */
export function updateDeviceGroup(body: Partial<DeviceGroupDto>) {
  return request<void>({ url: DEVICE_BASE + '/group', method: 'PUT', data: body });
}

/** 删除设备分组: DELETE /api/device/group/{id} */
export function removeDeviceGroup(id: string) {
  return request<void>({ url: DEVICE_BASE + '/group/' + id, method: 'DELETE' });
}

/** 设备类型列表: GET /api/device/type/list */
export function listDeviceTypes(params: { page?: number; pageSize?: number; keyword?: string }) {
  return request<PageResult<DeviceTypeDto>>({ url: DEVICE_BASE + '/type/list', method: 'GET', params });
}

/** 新增/编辑设备类型（合一）: POST /api/device/type */
export function saveDeviceType(body: DeviceTypeSaveRequest) {
  return request<DeviceTypeDto>({ url: DEVICE_BASE + '/type', method: 'POST', data: body });
}

/** 删除设备类型: DELETE /api/device/type/{id} */
export function removeDeviceType(id: string) {
  return request<void>({ url: DEVICE_BASE + '/type/' + id, method: 'DELETE' });
}

// ==================== 数据域 (/api/data) ====================

const DATA_BASE = '/data';

/** 设备传感器读数（时序）: GET /api/data/readings */
export function getReadings(params: ReadingQuery) {
  return request<IotReadingDto[]>({ url: DATA_BASE + '/readings', method: 'GET', params });
}

/** 最新读数: GET /api/data/readings/latest */
export function getLatestReadings(deviceId?: string) {
  return request<IotReadingDto[]>({ url: DATA_BASE + '/readings/latest', method: 'GET', params: { deviceId } });
}

/** 历史数据（分页）: GET /api/data/history */
export function getHistoryData(params: {
  page?: number;
  pageSize?: number;
  deviceNo?: string;
  startTime?: string;
  endTime?: string;
  metric?: string;
}) {
  return request<PageResult<IotReadingDto>>({ url: DATA_BASE + '/history', method: 'GET', params });
}

// ==================== 告警域 (/api/alert) ====================

const ALERT_BASE = '/alert';

/** 报警列表: GET /api/alert/list */
export function listAlerts(params: AlertListQuery) {
  return request<PageResult<IotAlertDto>>({ url: ALERT_BASE + '/list', method: 'GET', params });
}

/** 报警确认: PUT /api/alert/{id}/acknowledge */
export function acknowledgeAlert(id: string) {
  return request<void>({ url: ALERT_BASE + '/' + id + '/acknowledge', method: 'PUT' });
}

/** 报警解决: PUT /api/alert/{id}/resolve */
export function resolveAlert(id: string) {
  return request<void>({ url: ALERT_BASE + '/' + id + '/resolve', method: 'PUT' });
}

/** 报警规则列表: GET /api/alert/rule/list */
export function listAlertRules(params: { page?: number; pageSize?: number; keyword?: string }) {
  return request<PageResult<AlertRuleDto>>({ url: ALERT_BASE + '/rule/list', method: 'GET', params });
}

/** 创建报警规则: POST /api/alert/rule */
export function createAlertRule(body: Partial<AlertRuleDto>) {
  return request<void>({ url: ALERT_BASE + '/rule', method: 'POST', data: body });
}

/** 更新报警规则: PUT /api/alert/rule */
export function updateAlertRule(body: Partial<AlertRuleDto>) {
  return request<void>({ url: ALERT_BASE + '/rule', method: 'PUT', data: body });
}

/** 删除报警规则: DELETE /api/alert/rule/{id} */
export function removeAlertRule(id: string) {
  return request<void>({ url: ALERT_BASE + '/rule/' + id, method: 'DELETE' });
}

/** 通知渠道列表: GET /api/alert/notification/list */
export function listNotifyConfigs() {
  return request<NotifyConfigDto[]>({ url: ALERT_BASE + '/notification/list', method: 'GET' });
}

/** 保存通知渠道配置: POST /api/alert/notification */
export function saveNotifyConfig(body: Partial<NotifyConfigDto>) {
  return request<void>({ url: ALERT_BASE + '/notification', method: 'POST', data: body });
}

// ==================== AI域 (/api/ai) ====================

const AI_BASE = '/ai';

/**
 * AI 对话超时（2026-10-23）：RAG 链路 = bge-m3 向量化 + pgvector 混合检索 + 本地 Ollama
 * qwen2.5:1.5b 生成（CPU 推理约 2 token/s，实测一次问答 15s~3min），远超全局 15s 默认值，
 * 故单独放宽到 180s（与 aiEval.ts VERIFY_TIMEOUT_MS 同一模式）。
 */
const AI_CHAT_TIMEOUT_MS = 180000;

/** AI 对话: POST /api/ai/chat */
export function aiChat(body: AiChatRequest) {
  return request<AiConversationDto>({
    url: AI_BASE + '/chat',
    method: 'POST',
    data: body,
    timeout: AI_CHAT_TIMEOUT_MS,
  });
}

/** 对话历史: GET /api/ai/chat/history/{sessionId} */
export function getChatHistory(sessionId: string) {
  return request<AiConversationDto[]>({ url: AI_BASE + '/chat/history/' + sessionId, method: 'GET' });
}

/** 会话列表: GET /api/ai/chat/sessions */
export function getChatSessions() {
  return request<ChatSessionDto[]>({ url: AI_BASE + '/chat/sessions', method: 'GET' });
}

// ==================== AI 动作闸门 (/api/ai/actions) ====================

/** AI 动作日志 DTO */
export interface AiActionLogDto {
  id: string;
  actionType: string;
  agentCode: string | null;
  description: string | null;
  payload: string | null;
  status: string;
  confirmBy: string | null;
  rejectReason: string | null;
  result: string | null;
  confirmedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** AI 动作列表（可选 status 过滤：PENDING/CONFIRMED/REJECTED；缺省全部） */
export function listAiActions(status?: string) {
  return request<AiActionLogDto[]>({ url: AI_BASE + '/actions', method: 'GET', params: status ? { status } : undefined });
}

/** 确认执行 AI 动作: POST /api/ai/actions/{id}/confirm */
export function confirmAiAction(id: string) {
  return request<string>({ url: AI_BASE + '/actions/' + id + '/confirm', method: 'POST' });
}

/** 驳回 AI 动作: POST /api/ai/actions/{id}/reject?reason= */
export function rejectAiAction(id: string, reason: string) {
  return request<string>({ url: AI_BASE + '/actions/' + id + '/reject', method: 'POST', params: { reason } });
}

/** 知识库列表: GET /api/ai/kb/list */
export function listKnowledgeBases(params: KbListQuery) {
  return request<PageResult<KnowledgeBaseDto>>({ url: AI_BASE + '/kb/list', method: 'GET', params });
}

/** 创建知识库: POST /api/ai/kb */
export function createKnowledgeBase(body: Partial<KnowledgeBaseDto>) {
  return request<void>({ url: AI_BASE + '/kb', method: 'POST', data: body });
}

/** 更新知识库: PUT /api/ai/kb */
export function updateKnowledgeBase(body: Partial<KnowledgeBaseDto>) {
  return request<void>({ url: AI_BASE + '/kb', method: 'PUT', data: body });
}

/** 删除知识库: DELETE /api/ai/kb/{id} */
export function removeKnowledgeBase(id: string) {
  return request<void>({ url: AI_BASE + '/kb/' + id, method: 'DELETE' });
}

/** 知识库文档列表: GET /api/ai/kb/{kbId}/documents */
export function listKbDocuments(kbId: string) {
  return request<KbDocumentDto[]>({ url: AI_BASE + '/kb/' + kbId + '/documents', method: 'GET' });
}

/** 上传文档到知识库: POST /api/ai/kb/{kbId}/documents */
export function uploadKbDocument(kbId: string, formData: FormData) {
  return request<void>({ url: AI_BASE + '/kb/' + kbId + '/documents', method: 'POST', data: formData, headers: { 'Content-Type': 'multipart/form-data' } });
}

/** 删除知识库文档: DELETE /api/ai/kb/{kbId}/documents/{docId} */
export function removeKbDocument(kbId: string, docId: string) {
  return request<void>({ url: AI_BASE + '/kb/' + kbId + '/documents/' + docId, method: 'DELETE' });
}

/** 异常检测规则列表: GET /api/ai/anomaly-detection/list */
export function listAnomalyRules(params: { page?: number; pageSize?: number }) {
  return request<PageResult<AnomalyDetectionRuleDto>>({ url: AI_BASE + '/anomaly-detection/list', method: 'GET', params });
}

/** 创建异常检测规则: POST /api/ai/anomaly-detection */
export function createAnomalyRule(body: Partial<AnomalyDetectionRuleDto>) {
  return request<void>({ url: AI_BASE + '/anomaly-detection', method: 'POST', data: body });
}

/** 更新异常检测规则: PUT /api/ai/anomaly-detection */
export function updateAnomalyRule(body: Partial<AnomalyDetectionRuleDto>) {
  return request<void>({ url: AI_BASE + '/anomaly-detection', method: 'PUT', data: body });
}

/** 删除异常检测规则: DELETE /api/ai/anomaly-detection/{id} */
export function removeAnomalyRule(id: string) {
  return request<void>({ url: AI_BASE + '/anomaly-detection/' + id, method: 'DELETE' });
}

// ==================== AI 可追溯缺口补齐（② ③ ④ ⑤ ⑥） ====================

/** AI 报价草稿 DTO（#12a 落库 DRAFT，W5 询盘报价员） */
export interface AiQuoteDraftDto {
  id: string;
  inquiryId: string | null;
  category: string | null;
  unitPrice: number | string | null;
  quantityTons: number | string | null;
  marketBasisAmount: number | string | null;
  priceDate: string | null;
  status: string | null;
  quoteText: string | null;
  createdAt: string;
  updatedAt: string;
}

/** AI 报价草稿列表（可选 status / category 过滤） */
export function listAiQuoteDrafts(params?: { status?: string; category?: string }) {
  return request<AiQuoteDraftDto[]>({ url: AI_BASE + '/quote-draft', method: 'GET', params });
}
/** AI 报价草稿详情 */
export function getAiQuoteDraft(id: string) {
  return request<AiQuoteDraftDto>({ url: AI_BASE + '/quote-draft/' + id, method: 'GET' });
}

/** AI 编排 Plan 模板 DTO */
export interface AiAgentPlanDto {
  id: string;
  planCode: string | null;
  planName: string | null;
  planType: string | null;
  triggerIntent: string | null;
  triggerKeywords: string | null;
  steps: string | null;
  requiresHumanConfirm: boolean | null;
  description: string | null;
  version: number | null;
  status: number | null;
  runCount: number | null;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** AI 委派运行审计 DTO */
export interface AiAgentRunDto {
  id: string;
  planId: string | null;
  agentCode: string | null;
  action: string | null;
  inputSummary: string | null;
  outputSummary: string | null;
  citations: string | null;
  status: string | null;
  needsHumanConfirm: boolean | null;
  humanConfirmedBy: string | null;
  humanConfirmedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  latencyMs: number | null;
  createdAt: string | null;
}

/** AI 编排 Plan 列表 */
export function listAiAgentPlans() {
  return request<AiAgentPlanDto[]>({ url: AI_BASE + '/agent/plans', method: 'GET' });
}
/** AI 委派运行列表（可选 agentCode / status 过滤） */
export function listAiAgentRuns(params?: { agentCode?: string; status?: string }) {
  return request<AiAgentRunDto[]>({ url: AI_BASE + '/agent/runs', method: 'GET', params });
}

/** AI 意图路由规则 DTO */
export interface AiIntentRouteDto {
  id: string;
  routeCode: string | null;
  routeName: string | null;
  intent: string | null;
  matchType: string | null;
  pattern: string | null;
  priority: number | null;
  status: number | null;
  remark: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 意图路由规则列表 */
export function listIntentRoutes() {
  return request<AiIntentRouteDto[]>({ url: AI_BASE + '/intent-route', method: 'GET' });
}
/** 意图预演（不调 LLM、不落库） */
export function previewIntentRoute(question: string) {
  return request<Record<string, string>>({ url: AI_BASE + '/intent-route/preview', method: 'POST', data: { question } });
}
/** 刷新路由缓存（改库免重启） */
export function refreshIntentRoutes() {
  return request<string>({ url: AI_BASE + '/intent-route/refresh', method: 'POST' });
}
/** 启用/停用规则（真实改库） */
export function setIntentRouteStatus(id: string, status: number) {
  return request<string>({ url: AI_BASE + '/intent-route/' + id + '/status', method: 'POST', data: { status } });
}

/** KB 分块视图 DTO（不含 1024 维 embedding） */
export interface KbChunkViewDto {
  id: string;
  kbId: string | null;
  docId: string | null;
  content: string | null;
  chunkIndex: number | null;
  metadata: string | null;
  createdAt: string | null;
}
/** KB 入库日志 DTO */
export interface KbIngestLogDto {
  id: string;
  kbId: string | null;
  sourceId: string | null;
  docId: string | null;
  action: string | null;
  status: string | null;
  message: string | null;
  chunkCount: number | null;
  createdAt: string | null;
}

/** KB 分块列表（按 kbId，可选 docId） */
export function listKbChunks(kbId: string, docId?: string) {
  return request<KbChunkViewDto[]>({ url: AI_BASE + '/kb-trace/' + kbId + '/chunks', method: 'GET', params: docId ? { docId } : undefined });
}
/** KB 入库日志（按 kbId，可选 docId） */
export function listKbIngestLogs(kbId: string, docId?: string) {
  return request<KbIngestLogDto[]>({ url: AI_BASE + '/kb-trace/' + kbId + '/ingest-logs', method: 'GET', params: docId ? { docId } : undefined });
}

/** AI 已存图片 DTO（MinIO 真实对象） */
export interface StoredImageVoDto {
  id: string;
  fileName: string | null;
  category: string | null;
  size: number | null;
  createdAt: string | null;
  url: string | null;
}
/** AI 已存图片列表 */
export function listAiImages() {
  return request<StoredImageVoDto[]>({ url: AI_BASE + '/images', method: 'GET' });
}

// ==================== 柜↔设备绑定（经 blu-trade 代理 /container/{id}/iot/*） ====================

/** 封柜绑定：POST /container/{containerId}/iot/bind */
export function bindContainerDevice(req: ContainerBindRequest) {
  const { containerId, ...body } = req;
  return request<ContainerBindResp>({
    url: `/container/${containerId}/iot/bind`,
    method: 'POST',
    data: body,
  });
}

/** 解绑：POST /container/{containerId}/iot/unbind */
export function unbindContainerDevice(req: { containerId: string; deviceId: string }) {
  const { containerId, ...body } = req;
  return request<boolean>({
    url: `/container/${containerId}/iot/unbind`,
    method: 'POST',
    data: body,
  });
}

/** 柜 ACTIVE 绑定清单：GET /container/{containerId}/iot/active */
export function listActiveBinds(containerId: string) {
  return request<ActiveBindVo[]>({
    url: `/container/${containerId}/iot/active`,
    method: 'GET',
  });
}

/**
 * 柜实时聚合：GET /container/{containerId}/iot/realtime
 * @param endTime 可选截止时间（UTC ISO，带 Z）。订单已完成时传入履约末节点完成时间，
 *               后端改用 getLatestBefore 取截止时刻读数，避免显示过期活体值。
 */
export function getContainerRealtime(containerId: string, endTime?: string) {
  return request<ContainerRealtimeVo>({
    url: `/container/${containerId}/iot/realtime`,
    method: 'GET',
    params: endTime ? { endTime } : undefined,
  });
}

/** 到港温度报告：GET /container/{containerId}/iot/temp-report?arriveAt= */
export function getContainerTempReport(containerId: string, arriveAt: string) {
  return request<TempReportResult>({
    url: `/container/${containerId}/iot/temp-report`,
    method: 'GET',
    params: { arriveAt },
  });
}

export const containerBindApi = {
  bind: bindContainerDevice,
  unbind: unbindContainerDevice,
  listActive: listActiveBinds,
  realtime: getContainerRealtime,
  tempReport: getContainerTempReport,
};

// ==================== 聚合导出 ====================

export const iotDeviceApi = {
  list: listDevices,
  getById: getDevice,
  getShadow: getDeviceShadow,
  bind: bindDevice,
  save: saveDevice,
  remove: removeDevice,
  groups: { list: listDeviceGroups, create: createDeviceGroup, update: updateDeviceGroup, remove: removeDeviceGroup },
  types: { list: listDeviceTypes, save: saveDeviceType, remove: removeDeviceType },
};

export const iotDataApi = {
  readings: getReadings,
  latest: getLatestReadings,
  history: getHistoryData,
};

export const iotAlertApi = {
  list: listAlerts,
  acknowledge: acknowledgeAlert,
  resolve: resolveAlert,
  rules: { list: listAlertRules, create: createAlertRule, update: updateAlertRule, remove: removeAlertRule },
  notify: { list: listNotifyConfigs, save: saveNotifyConfig },
};

export const iotAiApi = {
  chat: aiChat,
  chatHistory: getChatHistory,
  sessions: getChatSessions,
  kb: { list: listKnowledgeBases, create: createKnowledgeBase, update: updateKnowledgeBase, remove: removeKnowledgeBase },
  documents: { list: listKbDocuments, upload: uploadKbDocument, remove: removeKbDocument },
  anomalyRules: { list: listAnomalyRules, create: createAnomalyRule, update: updateAnomalyRule, remove: removeAnomalyRule },
  quoteDraft: { list: listAiQuoteDrafts, get: getAiQuoteDraft },
  agent: { plans: listAiAgentPlans, runs: listAiAgentRuns },
  intentRoute: { list: listIntentRoutes, preview: previewIntentRoute, refresh: refreshIntentRoutes, setStatus: setIntentRouteStatus },
  kbTrace: { chunks: listKbChunks, ingestLogs: listKbIngestLogs },
  images: { list: listAiImages },
};
